import { useEffect, useState } from 'react';
import type { ListData } from '../../../types';
import usePlayer from '../contexts/usePlayer';
import useToast from '../contexts/useToast';
import PlusIcon from './icons/PlusIcon';
import CheckIcon from './icons/CheckIcon';
import PlayButton from './PlayButton';

type Props = {
  item: ListData;
  onAdd?: () => void;
};

const ItemActionButton = ({ item, onAdd }: Props) => {
  const { nowPlayingId, loadNext } = usePlayer();
  const { showToast } = useToast();
  const [isQueued, setIsQueued] = useState(Boolean(item.inQueue));
  const [justAdded, setJustAdded] = useState(false);
  const [isPending, setIsPending] = useState(false);

  const isNowPlaying = nowPlayingId === item.id;

  useEffect(() => {
    setIsQueued(Boolean(item.inQueue));
    setIsPending(false);
  }, [item.id, item.inQueue]);

  // When now playing, render a PlayButton instead
  if (isNowPlaying) {
    return <PlayButton item={item} size={36} />;
  }

  const handleAction = async () => {
    if (isQueued || isPending) return;
    setIsPending(true);

    // Capture this before the async call
    const queueWasEmpty = !nowPlayingId;

    try {
      const res = await fetch('/api/queue/add', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contentId: item.id }),
      });

      if (!res.ok) {
        showToast('Failed to add to queue.', 'error');
        setIsPending(false);
        return;
      }

      setIsQueued(true);
      setJustAdded(true);
      showToast('Added to queue', 'success');
      onAdd?.();

      // If queue was empty, load the item as nowPlaying (but don't auto-play)
      if (queueWasEmpty) {
        await loadNext();
      }

      setTimeout(() => {
        setJustAdded(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to add to queue', err);
      showToast('Failed to add to queue.', 'error');
      setIsPending(false);
    }
  };

  const isFailed = item.status === 'failed';
  const isDisabled = isQueued || isFailed || isPending;
  const iconColor = isFailed
    ? 'var(--gold-hover)'
    : justAdded
      ? 'var(--teal)'
      : isQueued
        ? 'var(--gold-hover)'
        : 'var(--gold)';

  return (
    <button
      className="pill-button add-to-queue-btn"
      onClick={(e) => {
        e.stopPropagation();
        handleAction();
      }}
      onTouchEnd={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleAction();
      }}
      disabled={isDisabled}
      aria-pressed={isQueued}
      aria-label={isQueued ? 'In queue' : 'Add to queue'}
      title={isQueued ? 'In Queue' : 'Add to Queue'}
      style={{
        padding: '8px',
        borderRadius: '50%',
        width: '36px',
        height: '36px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.3s ease',
        ...(isFailed
          ? {
              borderColor: 'var(--gold-hover)',
              cursor: 'default',
            }
          : justAdded
            ? {
                borderColor: 'var(--teal)',
                transform: 'scale(1.1)',
              }
            : isQueued
              ? {
                  borderColor: 'var(--gold-hover)',
                  cursor: 'default',
                }
              : {}),
      }}
    >
      <span
        style={{
          display: 'flex',
          transition: 'transform 0.3s ease',
          transform: justAdded ? 'scale(1.2)' : 'scale(1)',
        }}
      >
        {isQueued ? (
          <CheckIcon size={24} color={iconColor} />
        ) : (
          <PlusIcon size={24} color={iconColor} />
        )}
      </span>
    </button>
  );
};

export default ItemActionButton;
